import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Divider,
  TextField,
  Chip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faCheck,
  faXmark,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { VolumeItem } from "./VolumeItem";

export const VolumeCard = React.memo(
  ({ v, idx, onEditVolume, onRemoveVolume, onRemoveItem, mode = "create" }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(v.name);

    useEffect(() => {
      setEditName(v.name);
    }, [v.name]);

    // Se o volume tem shipment, está travado
    const isLocked = !!v.hasShipment;

    const handleSave = () => {
      if (editName.trim() === v.name) {
        setIsEditing(false);
        return;
      }
      const success = onEditVolume(v.identifier, editName);
      if (success) {
        setIsEditing(false);
      }
    };

    const handleCancel = () => {
      setEditName(v.name);
      setIsEditing(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") handleSave();
      if (e.key === "Escape") handleCancel();
    };

    // Mostra ações se: (modo edit OU não tem id) E NÃO está travado
    const showActions = (mode === "edit" || !v.id) && !isLocked;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "5px",
          px: 2,
          py: 1,
          border: `1px solid ${isLocked ? "#ff9800" : "#e0e0e0"}`,
          backgroundColor: isLocked ? "#fff8e1" : "transparent",
          "&:hover": { borderColor: isLocked ? "#ff9800" : "#1976d2" },
        }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ height: 32 }}>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            {isEditing && !isLocked ? (
              <TextField
                size="small"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                fullWidth
                variant="standard"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                    pb: 0.5,
                  },
                }}
              />
            ) : (
              <>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={isLocked ? "text.disabled" : "primary"}>
                  {v.name}
                </Typography>
                {isLocked && (
                  <Tooltip
                    title={`Volume vinculado ao carregamento #${v.shipmentId}. Remova-o do carregamento para editar.`}
                    placement="top">
                    <Chip
                      size="small"
                      icon={<FontAwesomeIcon icon={faLock} />}
                      label={`Carregamento #${v.shipmentId}`}
                      color="warning"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        "& .MuiChip-icon": { fontSize: "0.7rem" },
                      }}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </Box>

          <Box display="flex" alignItems="center">
            {isLocked ? (
              // Mostra apenas o ícone de cadeado quando travado
              <Tooltip
                title={`Bloqueado: vinculado ao carregamento #${v.shipmentId}`}
                placement="top">
                <IconButton size="small" disabled>
                  <FontAwesomeIcon icon={faLock} style={{ color: "#ff9800" }} />
                </IconButton>
              </Tooltip>
            ) : (
              showActions && (
                <>
                  {isEditing ? (
                    <>
                      <Tooltip title="Salvar Nome">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleSave}>
                          <FontAwesomeIcon icon={faCheck} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          color="default"
                          onClick={handleCancel}>
                          <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Editar Volume">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setIsEditing(true)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover Volume">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemoveVolume(v)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )
            )}
          </Box>
        </Box>

        {v.items?.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" gap={0.5}>
              {v.items.map((item, j) => (
                <VolumeItem
                  key={`${item.type}-${item.identify || item.id || j}`}
                  item={item}
                  onRemove={() => onRemoveItem(idx, j, v)}
                  locked={isLocked}
                  shipmentId={v.shipmentId}
                />
              ))}
            </Box>
          </>
        )}
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.v === nextProps.v &&
      prevProps.idx === nextProps.idx &&
      prevProps.onEditVolume === nextProps.onEditVolume &&
      prevProps.onRemoveVolume === nextProps.onRemoveVolume &&
      prevProps.onRemoveItem === nextProps.onRemoveItem &&
      prevProps.mode === nextProps.mode
    );
  },
);
